import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  Flex,
  Skeleton,
  Icon,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@kognitos/lattice';
import {
  getListAutomationsQueryKey,
  getListAutomationsInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  getGetNextCopyNameQueryKey,
  useDuplicateAutomation,
  useGetNextCopyName,
} from '@/generated/hooks/grimoire/v1/api';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models/v1AutomationStage';
import { useUserPreferencesStore } from '@/shared/stores/user-preferences.store';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';
import { AccessDeniedAlert } from '@/shared/components';
import { parseResourceName } from '@/shared/utils';
import { getRoutePath } from '@/routes/util';
import { useAutomationTriggers } from '@/modules/triggers';

import {
  createAutomationDuplicateSchema,
  type AutomationDuplicateFormData,
} from '@/modules/automations/schemas';

interface IDuplicateAutomationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Display name of the automation being duplicated. */
  title: string;
  automationId: string;
  stage: V1AutomationStage;
  onDuplicate?: () => void;
}

export function DuplicateAutomationDialog({
  open,
  onClose,
  title,
  automationId,
  stage,
  onDuplicate,
}: IDuplicateAutomationDialogProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { currentOrganizationId, currentWorkspaceId } =
    useUserPreferencesStore();

  const [isNavigating, setIsNavigating] = useState(false);

  const isDraft = stage === V1AutomationStage.AUTOMATION_STAGE_DRAFT;

  const { matchedTriggers, isLoading: isLoadingTriggers } =
    useAutomationTriggers({ automationId, isDraft, enabled: open });

  const hasTriggers = matchedTriggers.length > 0;

  const duplicateSchema = useMemo(
    () => createAutomationDuplicateSchema(tAutomation),
    [tAutomation],
  );

  const form = useForm<AutomationDuplicateFormData>({
    resolver: zodResolver(duplicateSchema),
    defaultValues: { displayName: '' },
  });

  // Ask the backend what name the copy would receive so we can pre-fill the
  // Name field. This is the single source of truth for copy naming (MAN-3801);
  // the duplicate operation would assign the same name.
  const { data: nextCopyNameResponse, isLoading: isLoadingCopyName } =
    useGetNextCopyName(
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      {
        query: { enabled: open },
      },
    );

  const suggestedCopyName = nextCopyNameResponse?.data.display_name ?? '';

  const { reset, formState } = form;

  // eslint-disable-next-line no-restricted-syntax -- reason: sync the async backend-computed copy name into the form once it loads
  useEffect(() => {
    // Pre-fill with the computed name once it loads, unless the user has
    // already edited the field.
    if (open && suggestedCopyName && !formState.isDirty) {
      reset({ displayName: suggestedCopyName });
    }
  }, [open, suggestedCopyName, formState.isDirty, reset]);

  const {
    mutate: duplicateAutomation,
    isPending: isDuplicateAutomationPending,
    isError: isDuplicateAutomationError,
    error: duplicateAutomationError,
    reset: resetDuplicateMutation,
  } = useDuplicateAutomation();

  const isLoading = isDuplicateAutomationPending || isNavigating;

  const handleClose = () => {
    form.reset({ displayName: '' });
    resetDuplicateMutation();
    onClose();
  };

  const handleDuplicate = (data: AutomationDuplicateFormData) => {
    const displayName = data.displayName?.trim();
    // Only send display_name when the user actually overrode the suggestion.
    // The backend uses a provided name verbatim (no disambiguation), while an
    // omitted name is computed fresh at create time. Echoing back the suggested
    // name risks sending a stale value that collides (e.g. a second
    // "Copy (2) of X"), so when it's unchanged we let the backend name it.
    const isOverride =
      !!displayName && displayName !== suggestedCopyName.trim();
    duplicateAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        data: { stage, ...(isOverride ? { display_name: displayName } : {}) },
      },
      {
        onSuccess: response => {
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          // The copy just created changes the next available name, so refresh
          // the suggestion for the next time the dialog opens.
          queryClient.invalidateQueries({
            queryKey: getGetNextCopyNameQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
            ),
          });

          const newAutomationName = response.data.automation?.name;
          if (!newAutomationName) return;
          const newAutomationId =
            parseResourceName(newAutomationName).automation;
          if (!newAutomationId) return;

          setIsNavigating(true);
          onClose();
          onDuplicate?.();
          navigate(
            getRoutePath('DRAFT_EDITOR', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              draftId: newAutomationId,
            }),
            { state: { focusTitle: true } },
          );
        },
      },
    );
  };

  const isAccessDeniedError = checkIsAccessDeniedError(
    duplicateAutomationError,
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="automation-duplicate-dialog" stopPropagation>
        <DialogHeader>
          <DialogTitle>{tAutomation('automation.duplicate')}</DialogTitle>
          <DialogDescription className="sr-only">
            {tAutomation(
              hasTriggers && isDraft
                ? 'automation.duplicateConfirmation.alertTitle.withTriggers'
                : 'automation.duplicateConfirmation.alertTitle.noTriggers',
            )}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleDuplicate)}>
            <Flex direction="column" gap={4}>
              {isLoadingTriggers ? (
                <Skeleton className="h-[88px] w-full rounded-lg" />
              ) : (
                <Alert variant="default">
                  <Icon type="Info" />
                  <AlertTitle>
                    {tAutomation(
                      hasTriggers && isDraft
                        ? 'automation.duplicateConfirmation.alertTitle.withTriggers'
                        : 'automation.duplicateConfirmation.alertTitle.noTriggers',
                    )}
                  </AlertTitle>
                  <AlertDescription>
                    {tAutomation(
                      hasTriggers
                        ? 'automation.duplicateConfirmation.description.withTriggers'
                        : 'automation.duplicateConfirmation.description.noTriggers',
                      { processTitle: title },
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      {tAutomation(
                        'automation.duplicateConfirmation.nameLabel',
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        data-testid="automation-duplicate-dialog-name"
                        placeholder={tAutomation(
                          'automation.duplicateConfirmation.namePlaceholder',
                          { processTitle: title },
                        )}
                        disabled={isLoading || isLoadingCopyName}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isDuplicateAutomationError &&
                (isAccessDeniedError ? (
                  <AccessDeniedAlert />
                ) : (
                  <Alert variant="destructive">
                    <AlertTitle>{tCommon('common.error.title')}</AlertTitle>
                    <AlertDescription>
                      {tAutomation('automation.duplicateConfirmation.error')}
                    </AlertDescription>
                  </Alert>
                ))}
            </Flex>
          </form>
        </Form>
        <DialogFooter>
          <Button
            onClick={form.handleSubmit(handleDuplicate)}
            disabled={isLoading}
            isLoading={isLoading}
            data-testid="automation-duplicate-dialog-confirm"
          >
            {tAutomation('automation.duplicate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
